/**
 * ChemistAI — pure behaviour tree for "The Chemist" Stage 5 boss.
 * No Phaser imports — fully testable in Node.js.
 *
 * Croydon Spice Lab chemist with flamethrower and acid grenades. Attack patterns:
 *   flame_cone       — close-range sustained flamethrower (telegraphed wind-up)
 *   acid_grenade_lob — arcing grenade at any range, leaves a lingering damage puddle
 *
 * Retreat-to-vat: after every ACID_LOBS_BEFORE_RETREAT acid lobs the Chemist
 * sprints away from the player to reposition near a vat hazard.
 *
 * Phase 1 (HP > 50%): round-robin flame_cone → acid_grenade_lob.
 * Phase 2 (HP ≤ 50%): same cycle with halved cooldowns (alternating pressure).
 */
import { type EnemyAIInput, ENGAGE_RANGE } from '@/systems/EnemyAI';

// ── Range constants ───────────────────────────────────────────────────────────
/** Flamethrower requires close quarters. */
export const FLAME_CONE_RANGE = 120;

// ── Timing constants ─────────────────────────────────────────────────────────
/** Chemist sparks the igniter before firing the flamethrower. */
export const FLAME_CONE_WINDUP_FRAMES = 22;
/** Chemist arcs arm overhead before lobbing the acid grenade. */
export const ACID_GRENADE_WINDUP_FRAMES = 18;
/** Retreat frames after a standard attack. */
export const BACKSTEP_FRAMES = 20;
/** Duration of the retreat-to-vat repositioning sprint. */
export const RETREAT_FRAMES = 45;
/** Speed multiplier during backstep. */
export const CHEMIST_BACKSTEP_SPEED = 1.3;
/** Speed multiplier during retreat-to-vat. */
export const CHEMIST_RETREAT_SPEED = 1.6;
const BASE_COOLDOWN = 80;

// ── HP thresholds ─────────────────────────────────────────────────────────────
/** HP fraction at which phase 2 (alternating pressure) activates. */
export const PHASE_2_THRESHOLD = 0.5;

// ── Retreat cadence ───────────────────────────────────────────────────────────
/** How many acid lobs the Chemist throws before retreating to a vat. */
export const ACID_LOBS_BEFORE_RETREAT = 2;

// ── Types ─────────────────────────────────────────────────────────────────────
export type ChemistPhase = 1 | 2;

export type ChemistAttack = 'flame_cone' | 'acid_grenade_lob';

export type ChemistNode =
  | 'scan'
  | 'approach'
  | 'flame_windup'
  | 'acid_windup'
  | 'retreating'
  | 'backstep';

export interface ChemistAIState {
  node: ChemistNode;
  phase: ChemistPhase;
  cooldown: number;
  windup: number;
  backstepFrames: number;
  retreatFrames: number;
  /** Round-robin: 0 = flame_cone, 1 = acid_grenade_lob. */
  attackCycle: number;
  /** Counts down acid lobs; when it reaches 0 the next acid lob triggers a retreat. */
  lobsUntilRetreat: number;
}

export interface ChemistAIInput extends EnemyAIInput {
  /** Current HP ÷ maxHp (0..1). */
  hpFraction: number;
}

export interface ChemistAIOutput {
  moveX: number;
  moveY: number;
  facing: 1 | -1;
  wantsAttack: boolean;
  attackType: ChemistAttack | null;
  nextState: ChemistAIState;
}

export function initialChemistState(): ChemistAIState {
  return {
    node: 'scan',
    phase: 1,
    cooldown: 0,
    windup: 0,
    backstepFrames: 0,
    retreatFrames: 0,
    attackCycle: 0,
    lobsUntilRetreat: ACID_LOBS_BEFORE_RETREAT,
  };
}

/**
 * Advance the Chemist behaviour tree by one frame.
 * Pure function — the Phaser adapter applies movement and spawns hitboxes /
 * lingering acid puddles based on the output.
 */
export function tickChemistAI(input: ChemistAIInput, state: ChemistAIState): ChemistAIOutput {
  const facing: 1 | -1 = input.targetX >= input.selfX ? 1 : -1;

  if (input.isKO || input.isBeingThrown) {
    return noOp(facing, initialChemistState());
  }

  const phase: ChemistPhase = input.hpFraction <= PHASE_2_THRESHOLD ? 2 : 1;

  // ── Tick counters ─────────────────────────────────────────────────────────
  const cooldown = Math.max(0, state.cooldown - 1);
  const windup = Math.max(0, state.windup - 1);
  const backstepFrames = Math.max(0, state.backstepFrames - 1);
  const retreatFrames = Math.max(0, state.retreatFrames - 1);

  // ── Hitstun: freeze animation, keep ticking cooldown ─────────────────────
  if (input.isHitstunned) {
    return noOp(facing, {
      ...state,
      phase,
      node: 'scan',
      cooldown,
      windup: 0,
      backstepFrames: 0,
      retreatFrames: 0,
    });
  }

  // ── RETREATING (retreat-to-vat reposition) ────────────────────────────────
  if (state.node === 'retreating') {
    if (retreatFrames > 0) {
      const { dx, dy, dist } = toTarget(input);
      const away = norm(-dx, -dy, dist);
      return {
        moveX: away.x * CHEMIST_RETREAT_SPEED,
        moveY: away.y * CHEMIST_RETREAT_SPEED,
        facing,
        wantsAttack: false,
        attackType: null,
        nextState: { ...state, phase, cooldown, retreatFrames },
      };
    }
    return noOp(facing, {
      ...state,
      phase,
      cooldown,
      node: 'scan',
      retreatFrames: 0,
    });
  }

  // ── BACKSTEP ──────────────────────────────────────────────────────────────
  if (state.node === 'backstep') {
    if (backstepFrames > 0) {
      const { dx, dy, dist } = toTarget(input);
      const away = norm(-dx, -dy, dist);
      return {
        moveX: away.x * CHEMIST_BACKSTEP_SPEED,
        moveY: away.y * CHEMIST_BACKSTEP_SPEED,
        facing,
        wantsAttack: false,
        attackType: null,
        nextState: { ...state, phase, cooldown, backstepFrames },
      };
    }
    return noOp(facing, {
      ...state,
      phase,
      cooldown,
      node: 'scan',
      backstepFrames: 0,
    });
  }

  // ── FLAME WINDUP ──────────────────────────────────────────────────────────
  if (state.node === 'flame_windup') {
    if (windup > 0) return noOp(facing, { ...state, phase, cooldown, windup });
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'flame_cone',
      nextState: {
        ...state,
        phase,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale, phase),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        retreatFrames: 0,
        attackCycle: nextCycle(state.attackCycle),
      },
    };
  }

  // ── ACID WINDUP ───────────────────────────────────────────────────────────
  if (state.node === 'acid_windup') {
    if (windup > 0) return noOp(facing, { ...state, phase, cooldown, windup });
    const nextLobsUntilRetreat = state.lobsUntilRetreat - 1;
    const willRetreat = nextLobsUntilRetreat <= 0;
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'acid_grenade_lob',
      nextState: {
        ...state,
        phase,
        node: willRetreat ? 'retreating' : 'backstep',
        cooldown: cooldownFor(input.aggressionScale, phase),
        windup: 0,
        backstepFrames: willRetreat ? 0 : BACKSTEP_FRAMES,
        retreatFrames: willRetreat ? RETREAT_FRAMES : 0,
        attackCycle: nextCycle(state.attackCycle),
        lobsUntilRetreat: willRetreat ? ACID_LOBS_BEFORE_RETREAT : nextLobsUntilRetreat,
      },
    };
  }

  // ── SCAN / APPROACH → select next attack ─────────────────────────────────
  const { dist } = toTarget(input);
  const { attackCycle } = state;

  if (cooldown === 0 && input.canAttack) {
    if (attackCycle === 0) {
      // flame_cone: requires close range
      if (dist <= FLAME_CONE_RANGE) {
        return noOp(facing, {
          ...state,
          phase,
          cooldown,
          node: 'flame_windup',
          windup: FLAME_CONE_WINDUP_FRAMES,
        });
      }
    } else {
      // attackCycle === 1: acid_grenade_lob fires at any range
      return noOp(facing, {
        ...state,
        phase,
        cooldown,
        node: 'acid_windup',
        windup: ACID_GRENADE_WINDUP_FRAMES,
      });
    }
  }

  // ── Approach orbit position ───────────────────────────────────────────────
  const orbitX = input.targetX + input.orbitOffsetX;
  const orbitY = input.targetY + input.orbitOffsetY;
  const odx = orbitX - input.selfX;
  const ody = orbitY - input.selfY;
  const orbitDist = Math.hypot(odx, ody);

  if (orbitDist > 8) {
    const speedMul = 1 + input.aggressionScale * 0.3;
    const move = norm(odx, ody, orbitDist);
    const node: ChemistNode = dist > ENGAGE_RANGE ? 'scan' : 'approach';
    return {
      moveX: move.x * speedMul,
      moveY: move.y * speedMul,
      facing,
      wantsAttack: false,
      attackType: null,
      nextState: {
        ...state,
        phase,
        cooldown,
        node,
        windup: 0,
        backstepFrames: 0,
        retreatFrames: 0,
      },
    };
  }

  return noOp(facing, { ...state, phase, cooldown, node: 'scan' });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function cooldownFor(aggressionScale: number, phase: ChemistPhase): number {
  const phaseScale = phase >= 2 ? 0.5 : 1.0;
  return Math.round(BASE_COOLDOWN * phaseScale * (1 - aggressionScale * 0.5));
}

function toTarget(input: ChemistAIInput): { dx: number; dy: number; dist: number } {
  const dx = input.targetX - input.selfX;
  const dy = input.targetY - input.selfY;
  return { dx, dy, dist: Math.hypot(dx, dy) };
}

function norm(x: number, y: number, len: number): { x: number; y: number } {
  const d = len || 1;
  return { x: x / d, y: y / d };
}

function noOp(facing: 1 | -1, nextState: ChemistAIState): ChemistAIOutput {
  return {
    moveX: 0,
    moveY: 0,
    facing,
    wantsAttack: false,
    attackType: null,
    nextState,
  };
}

function nextCycle(c: number): number {
  return (c + 1) % 2;
}
